import { useMemo } from 'react';

import { omit } from 'lodash-es';

import type { SessionUserRole } from 'src/shared/session';

import { useMappedLoadedState } from 'src/app/shared/hooks/state';

import { useCurrentUser, useCurrentUserAsync } from './currentUser';
import type { AsyncState } from './shared/types';

type Roles = Readonly<Record<SessionUserRole, boolean>>;

const FALLBACK: Roles = {
	staff: false,
	staffAdmin: false,
	support: false,
	audit: false,
	auditAdmin: false,
	petitionAdmin: false,
	engineering: false,
	product: false,
	campaigns: false,
	data: false,
	policy: false,
	policyAdmin: false,
	policySenior: false,
	supportAdmin: false,
	supportSenior: false,
	userResearch: false,
	communications: false,
};

export function useCurrentUserRoles(): Roles {
	const roles = useCurrentUser()?.roles;
	return useMemo(() => (roles ? (omit(roles, ['id', 'countryTeam', 'countryTeamAdmin']) as Roles) : FALLBACK), [roles]);
}
export function useCurrentUserRolesAsync(): AsyncState<Roles> {
	return useMappedLoadedState(useCurrentUserAsync(), ({ value }) => ({
		value: value?.roles ? (omit(value.roles, ['id', 'countryTeam', 'countryTeamAdmin']) as Roles) : FALLBACK,
	}));
}

export function useCurrentUserRole(name: SessionUserRole): boolean {
	return useCurrentUserRoles()[name];
}
export function useCurrentUserRoleAsync(name: SessionUserRole): AsyncState<boolean> {
	return useMappedLoadedState(useCurrentUserRolesAsync(), ({ value }) => ({ value: value[name] }));
}

export function useIsStarter(): boolean {
	return useCurrentUserRole('petitionAdmin');
}
export function useIsStarterAsync(): AsyncState<boolean> {
	return useCurrentUserRoleAsync('petitionAdmin');
}

export function useIsStaff(): boolean {
	return useCurrentUserRole('staff');
}
export function useIsStaffAsync(): AsyncState<boolean> {
	return useCurrentUserRoleAsync('staff');
}
